import React, { useContext, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Button as ListItemButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  makeStyles,
} from '@material-ui/core';
import {
  Launch,
  ContactSupport,
  Accessibility,
  HowToVote,
  KeyboardHide,
  GetApp,
  Print,
} from '@material-ui/icons';
import { AutoStoriesIcon, GroupsIcon } from 'icons';
import {
  AnalyticsModifiersContext,
  MobileAppValuesContext,
  UserSessionValuesContext,
} from 'contexts';
import { AdditionalMenus } from 'constants';

const useStyles = makeStyles((theme) => {
  return {
    list: {
      paddingTop: 0,
      paddingBottom: theme.spacing(2.5),
      overflowY: 'scroll',
    },
    listItem: {
      display: 'block',
      padding: 0,
    },
    subheader: {
      color: theme.palette.greyBlue[900],
      lineHeight: theme.typography.body2.lineHeight,
      marginRight: theme.spacing(4),
      padding: theme.spacing(2),
    },
    subList: {
      padding: 0,
    },
    subListItem: {
      display: 'block',
      padding: 0,
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
    },
    subListItemButton: {
      margin: theme.spacing(0, 0.125),
      padding: theme.spacing(1, 2),
      width: `calc(100% - ${theme.spacing(0.25)}px)`,
      '&:focus': {
        backgroundColor: '#F5F5F5',
        borderRadius: 0,
        outlineOffset: theme.spacing(-0.125),
      },
    },
    subListItemIcon: {
      minWidth: theme.spacing(3),
      minHeight: theme.spacing(3),
      color: theme.palette.greyBlue[600],
    },
    subListItemText: {
      paddingLeft: theme.spacing(1.25),
      color: theme.palette.greyBlue[700],
      textAlign: 'left',
    },
  };
});

const SubListItem = ({ item, lastOpenedMenuRef }) => {
  const classes = useStyles();

  if (item.component) {
    return (
      <ListItem className={classes.subListItem} id={item.id}>
        {item.component}
      </ListItem>
    );
  } else if (item.link) {
    return (
      <ListItem
        className={classes.subListItem}
        id={item.id}
        onClick={item.onClick}
      >
        <ListItem
          button
          className={classes.subListItemButton}
          component="a"
          href={item.link}
          ref={item.ref}
          rel="noreferrer"
          target="_blank"
        >
          {item.icon ? (
            <ListItemIcon className={classes.subListItemIcon}>
              {item.icon}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={item.title}
            className={classes.subListItemText}
          />
          <Launch className={classes.subListItemIcon} />
        </ListItem>
      </ListItem>
    );
  } else {
    return (
      <ListItem
        className={classes.subListItem}
        id={item.id}
        {...(lastOpenedMenuRef ? { ref: lastOpenedMenuRef } : {})}
      >
        <ListItemButton
          className={classes.subListItemButton}
          disableRipple
          onClick={item.onClick}
        >
          {item.icon ? (
            <ListItemIcon className={classes.subListItemIcon}>
              {item.icon}
            </ListItemIcon>
          ) : null}
          <ListItemText
            className={classes.subListItemText}
            primary={item.title}
          />
        </ListItemButton>
      </ListItem>
    );
  }
};

const HelpMenu = ({
  className,
  lastAdditionalMenuOpened = null,
  onCollaborationClick = () => {},
  onOfflineClick = () => {},
  onPrintClick = () => {},
  onShortcutsClick = () => {},
  ...props
}) => {
  const analytics = useContext(AnalyticsModifiersContext);
  const { isMobileApp } = useContext(MobileAppValuesContext);
  const {
    backToShelfInfo: { backToLabel, backToUrl },
    organization,
  } = useContext(UserSessionValuesContext);

  const isFollettUser = organization === 'follettcorp';

  const classes = useStyles();
  const lastOpenedMenuRef = useRef();

  const onSubmitATicketClicked = () => analytics.submitATicketClicked();
  const onMyShelfClicked = () => analytics.myShelfClicked();

  // Follett users should be redirected to Brytewave support resources
  const faqsTitle = isFollettUser ? 'Solve (FAQs)' : 'RedShelf Solve (FAQs)';
  const faqsLink = isFollettUser
    ? 'https://brytewavesolve.zendesk.com/hc/en-us'
    : 'https://solve.redshelf.com/hc/en-us';
  const ticketLink = isFollettUser
    ? 'mailto:customerservice@brytewave.com'
    : 'https://solve.redshelf.com/hc/en-us/requests/new';

  const helpMenuSections = [
    {
      id: 'offline',
      subheader: 'Offline',
      items: [
        {
          id: 'dfo',
          title: 'Download for Offline',
          menuName: AdditionalMenus.Offline,
          icon: <GetApp />,
          onClick: onOfflineClick,
        },
      ],
    },
    {
      id: 'print',
      subheader: 'Print',
      items: [
        {
          id: 'pptpdf',
          title: 'Print Pages to a PDF',
          menuName: AdditionalMenus.Print,
          icon: <Print />,
          onClick: onPrintClick,
        },
      ],
    },
    {
      id: 'collaboration',
      subheader: 'Collaboration',
      items: [
        {
          id: 'nsh',
          title: 'Note Sharing',
          menuName: AdditionalMenus.Collaboration,
          icon: <GroupsIcon />,
          onClick: onCollaborationClick,
        },
      ],
    },
    {
      id: 'shortcuts',
      subheader: 'Keyboard Shortcuts',
      items: [
        {
          id: 'keyboard-shortcuts',
          title: 'Manage Keyboard Shortcuts',
          menuName: AdditionalMenus.Keyboard,
          icon: <KeyboardHide />,
          onClick: onShortcutsClick,
        },
      ],
    },
    {
      id: 'help',
      subheader: 'Help',
      items: [
        {
          id: 'faqs',
          title: faqsTitle,
          icon: <ContactSupport />,
          link: faqsLink,
          endIcon: <Launch />,
        },
        {
          id: 'accessibility',
          title: 'Accessibility Support',
          icon: <Accessibility />,
          endIcon: <Launch />,
          link: 'https://solve.redshelf.com/hc/en-us/categories/360004189334-Accessibility-Resources-for-Students-and-Campus-Personnel',
        },
        {
          id: 'submit',
          title: 'Submit a Ticket',
          icon: <HowToVote />,
          endIcon: <Launch />,
          onClick: onSubmitATicketClicked,
          link: ticketLink,
        },
      ],
    },
  ];

  const originLabel = backToLabel ? backToLabel : 'MyShelf';
  Boolean(backToUrl) &&
    helpMenuSections.unshift({
      id: 'material',
      subheader: 'Material Management',
      items: [
        {
          id: 'my-shelf',
          title: originLabel,
          icon: <AutoStoriesIcon />,
          link: backToUrl,
          onClick: onMyShelfClicked,
          endIcon: <Launch />,
        },
      ],
    });

  const helpMenuSectionsToHideInMobile = [
    'offline',
    'print',
    'shortcuts',
    'versions',
    'help',
  ];

  const helpMenuSectionsToRender = isMobileApp
    ? helpMenuSections.filter(
        (section) => !helpMenuSectionsToHideInMobile?.includes(section.id),
      )
    : helpMenuSections;

  useEffect(
    function focusLastOpenedMenu() {
      if (!lastAdditionalMenuOpened && !lastOpenedMenuRef.current) {
        return;
      }

      lastOpenedMenuRef.current.querySelector('button')?.focus();
    },
    [lastAdditionalMenuOpened],
  );

  return (
    <List className={clsx(classes.list, className)} {...props}>
      {helpMenuSectionsToRender.map((section) => (
        <ListItem className={classes.listItem} key={`section-${section.id}`}>
          <ListSubheader
            className={classes.subheader}
            component="h3"
            disableSticky
            id={`section-${section.id}`}
            variant="body2"
          >
            {section.subheader}
          </ListSubheader>

          <List className={classes.subList}>
            {section.items.map((item) => (
              <SubListItem
                item={item}
                key={`item-${item.id}`}
                {...(item.menuName === lastAdditionalMenuOpened
                  ? { lastOpenedMenuRef: lastOpenedMenuRef }
                  : {})}
              />
            ))}
          </List>
        </ListItem>
      ))}
    </List>
  );
};

HelpMenu.propTypes = {
  className: PropTypes.string,
  lastAdditionalMenuOpened: PropTypes.string,
  onCollaborationClick: PropTypes.func,
  onOfflineClick: PropTypes.func,
  onPrintClick: PropTypes.func,
  onShortcutsClick: PropTypes.func,
};

export default HelpMenu;
